import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Upload, FileText, Download, CheckSquare, Square } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesManager = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    accent_color: 'accent-purple',
    duration: '',
    countries: '',
    button_text: 'Apply Now',
    display_order: 0,
    is_active: true,
    deliverables: [],
    base_rate: 0,
    agreement_terms: '',
    document_checklist: [],
    attachments: []
  });
  const [newDeliverable, setNewDeliverable] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/admin/services`,
        { headers: getAuthHeader() }
      );
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const serviceData = {
      ...formData,
      countries: formData.countries.split(',').map(c => c.trim()),
      deliverables: formData.deliverables,
      base_rate: parseFloat(formData.base_rate) || 0,
      agreement_terms: formData.agreement_terms
    };

    try {
      if (editingService) {
        await axios.put(
          `${API}/admin/services/${editingService.id}`,
          serviceData,
          { headers: getAuthHeader() }
        );
        toast({ title: "Success", description: "Service updated successfully" });
      } else {
        await axios.post(
          `${API}/admin/services`,
          serviceData,
          { headers: getAuthHeader() }
        );
        toast({ title: "Success", description: "Service created successfully" });
      }
      
      closeModal();
      fetchServices();
    } catch (error) {
      console.error('Failed to save service:', error);
      toast({
        title: "Error",
        description: "Failed to save service",
        variant: "destructive"
      });
    }
  };

  const deleteService = async (id) => {
    const confirmed = await window.confirm('Are you sure you want to delete this service?');
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/services/${id}`,
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Service deleted successfully" });
      fetchServices();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast({
        title: "Error",
        description: "Failed to delete service",
        variant: "destructive"
      });
    }
  };

  const openModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        title: service.title,
        description: service.description,
        category: service.category,
        accent_color: service.accent_color,
        duration: service.duration,
        countries: service.countries.join(', '),
        button_text: service.button_text || 'Apply Now',
        display_order: service.display_order,
        is_active: service.is_active,
        deliverables: service.deliverables || [],
        base_rate: service.base_rate || 0,
        agreement_terms: service.agreement_terms || '',
        document_checklist: service.document_checklist || [],
        attachments: service.attachments || []
      });
    } else {
      setEditingService(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        accent_color: 'accent-purple',
        duration: '',
        countries: '',
        button_text: 'Apply Now',
        display_order: services.length + 1,
        is_active: true,
        deliverables: [],
        base_rate: 0,
        agreement_terms: '',
        document_checklist: [],
        attachments: []
      });
    }
    setShowModal(true);
  };

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData({
        ...formData,
        deliverables: [...formData.deliverables, newDeliverable.trim()]
      });
      setNewDeliverable('');
    }
  };

  const removeDeliverable = (index) => {
    setFormData({
      ...formData,
      deliverables: formData.deliverables.filter((_, i) => i !== index)
    });
  };


  // Document Checklist functions
  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const newItem = {
        item: newChecklistItem.trim(),
        required: true,
        order: formData.document_checklist.length + 1
      };
      setFormData({
        ...formData,
        document_checklist: [...formData.document_checklist, newItem]
      });
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index) => {
    setFormData({
      ...formData,
      document_checklist: formData.document_checklist.filter((_, i) => i !== index)
    });
  };

  const toggleChecklistRequired = (index) => {
    const updatedChecklist = [...formData.document_checklist];
    updatedChecklist[index].required = !updatedChecklist[index].required;
    setFormData({
      ...formData,
      document_checklist: updatedChecklist
    });
  };

  // File attachment functions
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 10MB",
        variant: "destructive"
      });
      return;
    }

    if (!editingService) {
      toast({
        title: "Error",
        description: "Please save the service first before uploading files",
        variant: "destructive"
      });
      return;
    }

    setUploadingFile(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/services/${editingService.id}/upload-attachment`,
        formDataUpload,
        { 
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({ title: "Success", description: "File uploaded successfully" });
      
      // Add to formData attachments
      setFormData({
        ...formData,
        attachments: [...formData.attachments, response.data.attachment]
      });

      // Refresh services list
      fetchServices();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setUploadingFile(false);
      e.target.value = ''; // Reset file input
    }
  };

  const deleteAttachment = async (attachmentId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this attachment?');
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/services/${editingService.id}/attachments/${attachmentId}`,
        { headers: getAuthHeader() }
      );

      toast({ title: "Success", description: "Attachment deleted successfully" });

      // Remove from formData
      setFormData({
        ...formData,
        attachments: formData.attachments.filter(att => att.id !== attachmentId)
      });

      // Refresh services list
      fetchServices();
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast({
        title: "Error",
        description: "Failed to delete attachment",
        variant: "destructive"
      });
    }
  };


  const closeModal = () => {
    setShowModal(false);
    setEditingService(null);
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Services</h1>
          <p className="body-large">Manage visa services</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus size={20} />
          <span>Add Service</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading services...</p>
        </div>
      ) : (
        <div className="services-grid-admin">
          {services.map(service => (
            <div key={service.id} className={`service-card-admin ${service.accent_color}`}>
              <div className="service-card-header">
                <h3 className="heading-3">{service.title}</h3>
                <div className="card-actions">
                  <button onClick={() => openModal(service)} className="icon-btn">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => deleteService(service.id)} className="icon-btn delete-btn">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="body-small">{service.description}</p>
              <div className="service-meta">
                <span className="caption">Category: {service.category}</span>
                <span className="caption">Duration: {service.duration}</span>
                <span className="caption">Status: {service.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="heading-2">{editingService ? 'Edit Service' : 'Add Service'}</h2>
              <button onClick={closeModal} className="icon-btn">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="form-input"
                  rows="3"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Duration</label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="form-input"
                    placeholder="e.g., 2-4 weeks"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Countries (comma separated)</label>
                <input
                  type="text"
                  value={formData.countries}
                  onChange={(e) => setFormData({...formData, countries: e.target.value})}
                  className="form-input"
                  placeholder="USA, UK, Canada"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Button Text</label>
                <input
                  type="text"
                  value={formData.button_text}
                  onChange={(e) => setFormData({...formData, button_text: e.target.value})}
                  className="form-input"
                  placeholder="Apply Now"
                />
                <p className="caption">This button text will be used if no country services are linked</p>
              </div>

              <div className="form-group">
                <label className="form-label">Base Rate (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.base_rate}
                  onChange={(e) => setFormData({...formData, base_rate: e.target.value})}
                  className="form-input"
                  placeholder="0.00"
                />
                <p className="caption">Default rate for proposals</p>
              </div>

              <div className="form-group">
                <label className="form-label">Deliverables (for proposals)</label>
                <div className="space-y-2 mb-2">
                  {formData.deliverables.map((deliverable, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <span className="flex-1 text-sm">{deliverable}</span>
                      <button
                        type="button"
                        onClick={() => removeDeliverable(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDeliverable}
                    onChange={(e) => setNewDeliverable(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliverable())}
                    className="form-input flex-1"
                    placeholder="e.g., Document verification"
                  />
                  <button
                    type="button"
                    onClick={addDeliverable}
                    className="btn-secondary"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <p className="caption mt-1">Add deliverables that will be shown in proposals</p>
              </div>

              {/* Agreement Terms */}
              <div className="form-group">
                <label className="form-label">Agreement Terms & Conditions</label>
                <textarea
                  value={formData.agreement_terms}
                  onChange={(e) => setFormData({...formData, agreement_terms: e.target.value})}
                  className="form-input"
                  rows={8}
                  placeholder="Enter agreement-specific terms and conditions..."
                />
                <p className="caption mt-1">These terms will be used in agreements (not in proposals)</p>
              </div>

              {/* Document Checklist for Welcome Onboarding */}
              <div className="form-group">
                <label className="form-label">
                  <FileText size={16} style={{ display: 'inline', marginRight: '6px' }} />
                  Document Checklist (for Welcome Email)
                </label>
                <div style={{ marginBottom: '12px' }}>
                  {formData.document_checklist.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      marginBottom: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      gap: '8px'
                    }}>
                      <button
                        type="button"
                        onClick={() => toggleChecklistRequired(index)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                      >
                        {item.required ? <CheckSquare size={18} color="#10b981" /> : <Square size={18} color="#6b7280" />}
                      </button>
                      <span style={{ flex: 1, fontSize: '14px' }}>{item.item}</span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {item.required ? 'Required' : 'Optional'}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeChecklistItem(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                    placeholder="e.g., Valid Passport (minimum 6 months validity)"
                    className="form-input"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={addChecklistItem}
                    className="btn-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Plus size={16} /> Add Item
                  </button>
                </div>
                <p className="caption mt-1">
                  Documents required from client. Click checkbox to toggle required/optional status.
                </p>
              </div>

              {/* Service Attachments */}
              <div className="form-group">
                <label className="form-label">
                  <Upload size={16} style={{ display: 'inline', marginRight: '6px' }} />
                  Service Attachments (Forms & Templates)
                </label>
                <div style={{ marginBottom: '12px' }}>
                  {formData.attachments.map((attachment) => (
                    <div key={attachment.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      marginBottom: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      gap: '12px'
                    }}>
                      <FileText size={20} color="#6366f1" />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '500' }}>{attachment.filename}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {(attachment.file_size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteAttachment(attachment.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {formData.attachments.length === 0 && (
                    <p style={{ 
                      padding: '20px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '14px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px'
                    }}>
                      No attachments uploaded yet
                    </p>
                  )}
                </div>
                <input
                  type="file"
                  id="service-file-upload"
                  style={{ display: 'none' }}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.txt"
                  onChange={handleFileUpload}
                  disabled={!editingService || uploadingFile}
                />
                <label
                  htmlFor="service-file-upload"
                  className="btn-secondary"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: editingService && !uploadingFile ? 'pointer' : 'not-allowed',
                    opacity: editingService && !uploadingFile ? 1 : 0.5
                  }}
                >
                  <Upload size={16} />
                  {uploadingFile ? 'Uploading...' : 'Upload File'}
                </label>
                <p className="caption mt-1">
                  {editingService 
                    ? 'Upload forms, templates, or documents (PDF, DOCX, XLSX, TXT - Max 10MB)'
                    : 'Save the service first to enable file uploads'
                  }
                </p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Accent Color</label>
                  <select
                    value={formData.accent_color}
                    onChange={(e) => setFormData({...formData, accent_color: e.target.value})}
                    className="form-input"
                  >
                    <option value="accent-purple">Purple</option>
                    <option value="accent-blue">Blue</option>
                    <option value="accent-orange">Orange</option>
                    <option value="accent-pink">Pink</option>
                    <option value="accent-green">Green</option>
                    <option value="accent-grey">Grey</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span>Active (visible on website)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingService ? 'Update' : 'Create'} Service
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;